// ./src/components/features/ServicePoints/ServicePointsEditor.tsx
//
// ROADMAP #169 — the ONE editor for a list of service points (entrances, desks, merch tables, bars): the venue drawer's
// points (reused by every event there) and an event's own points (an event without a saved venue). Rows in order —
// name, kind, and for a venue the address it is at — saved WHOLE by the caller; the server refuses each problem at its
// row (`<list>[<index>].<field>`, read with `getIndexedFormErrors`). #176 — each point also says what is done at it
// (check-in / hand-out / both), shown as its kind's until changed; the kind's own is stored as null (follows the kind). Composed from Mantine primitives like the venue's
// address rows (VenueAddressesEditor) — the app has no generic array editor.
// #68 (owner, 2026-10-23) — and what GUESTS are told about a point, folded under each row ("What guests see"): a number
// to call, how to find it, where it is (the phone's position, set standing there), a picture. The lost & found block
// shows them for its desk; any page that sends a guest to a point can.
import React, { useCallback, useState } from "react";
import { ActionIcon, Box, Button, Collapse, Group, Paper, SimpleGrid, Stack, Text, TextInput, Textarea, Tooltip } from "@mantine/core";

import { AppIcon, MapPinDrawer, MediaImageField, SelectField } from "@/components/generic";
import { pointActivity, SERVICE_POINT_ACTIVITY_OPTIONS, SERVICE_POINT_KIND, SERVICE_POINT_KIND_OPTIONS } from "@/utils/servicePoints";

import { createEmptyServicePoint, rowWithActivity, rowWithKind, ServicePointRow } from "./servicePointRows";

export * from "./servicePointRows";

export interface ServicePointsEditorProps {
  value: ServicePointRow[];
  onChange: (rows: ServicePointRow[]) => void;
  /** Row-scoped refusals from the server (`getIndexedFormErrors(error, list).byIndex`). */
  errors?: Record<number, Record<string, string>>;
  /** A venue's SAVED addresses (`{ value: id, label }`) — offered when the venue has more than one; omit for an event. */
  addresses?: Array<{ value: string; label: string }>;
  disabled?: boolean;
  /** Shown when there are no points yet. */
  emptyText?: string;
}

export const ServicePointsEditor: React.FC<ServicePointsEditorProps> = ({ value, onChange, errors = {}, addresses, disabled = false, emptyText }) => {
  const showAddress = !!addresses && addresses.length > 1;

  const patch = useCallback(
    (index: number, next: Partial<ServicePointRow>) => {
      onChange(value.map((row, i) => (i === index ? { ...row, ...next } : row)));
    },
    [value, onChange],
  );

  const remove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  const add = useCallback(() => {
    onChange([...value, createEmptyServicePoint()]);
  }, [value, onChange]);

  // Which rows show "What guests see" — open by itself where a detail is set, or a refusal points into it.
  const [openDetails, setOpenDetails] = useState<Record<string, boolean>>({});
  const hasDetails = (row: ServicePointRow): boolean => !!(row.phone.trim() || row.guestNote.trim() || row.address.trim() || row.latitude != null || row.photo);
  // The row whose pin is being set on the map (local: the drawer gives the pin back to this form, which saves it).
  const [pinRowKey, setPinRowKey] = useState<string | null>(null);
  const pinIndex = pinRowKey ? value.findIndex((r) => r.key === pinRowKey) : -1;
  const pinRow = pinIndex >= 0 ? value[pinIndex] : null;
  // Without a pin of its own, the map opens where another point of the list is.
  const mapCenter = (() => {
    const other = value.find((r) => r.key !== pinRowKey && r.latitude != null && r.longitude != null);

    return other ? { latitude: other.latitude as number, longitude: other.longitude as number } : null;
  })();
  const detailError = (index: number): boolean =>
    !!(errors[index]?.phone || errors[index]?.guestNote || errors[index]?.address || errors[index]?.latitude || errors[index]?.photoUploadedFileId);

  return (
    <Stack gap="sm">
      {value.length === 0 && (
        <Text size="md" c="dimmed">
          {emptyText ?? "No points yet. Add the entrances, desks and tables where your crew scans."}
        </Text>
      )}

      {value.map((row, index) => (
        <Paper key={row.key} withBorder radius="md" p="sm" data-service-point-row={index}>
          <Group align="flex-start" wrap="nowrap" gap="xs">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" style={{ flex: 1, minWidth: 0 }}>
              <TextInput
                size="md"
                label="Name"
                placeholder="e.g. North gate"
                value={row.name}
                onChange={(e) => {
                  const name = e.currentTarget.value;
                  patch(index, { name });
                }}
                error={errors[index]?.name}
                disabled={disabled}
                maxLength={128}
              />
              <SelectField
                size="md"
                label="Kind"
                data={SERVICE_POINT_KIND_OPTIONS}
                value={row.kind}
                onChange={(_name, v) => patch(index, rowWithKind(row, v ?? SERVICE_POINT_KIND.ENTRANCE))}
                allowDeselect={false}
                error={errors[index]?.kind}
                disabled={disabled}
              />
              <SelectField
                size="md"
                label="What's done here"
                data={SERVICE_POINT_ACTIVITY_OPTIONS}
                value={pointActivity(row)}
                onChange={(_name, v) => patch(index, rowWithActivity(row, v))}
                allowDeselect={false}
                error={errors[index]?.activity}
                disabled={disabled}
              />
              {showAddress && (
                <SelectField
                  size="md"
                  label="At"
                  placeholder="The venue as a whole"
                  data={addresses ?? []}
                  value={row.venueAddressId}
                  onChange={(_name, v) => patch(index, { venueAddressId: v ?? null })}
                  clearable
                  error={errors[index]?.venueAddressId}
                  disabled={disabled}
                />
              )}
            </SimpleGrid>
            <Tooltip label="Remove point" withArrow>
              <ActionIcon
                variant="subtle"
                color="red"
                size={44}
                mt={28}
                onClick={() => remove(index)}
                disabled={disabled}
                aria-label={`Remove ${row.name.trim() || `point ${index + 1}`}`}
              >
                <AppIcon iconName="Trash" size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Box mt="xs">
            <Button
              variant="subtle"
              size="compact-md"
              px={0}
              onClick={() => setOpenDetails((o) => ({ ...o, [row.key]: !(o[row.key] ?? (hasDetails(row) || detailError(index))) }))}
              leftSection={<AppIcon iconName={(openDetails[row.key] ?? (hasDetails(row) || detailError(index))) ? "ChevronDown" : "ChevronRight"} size={16} />}
            >
              What guests see{hasDetails(row) ? "" : " — phone, how to find it, location, photo"}
            </Button>
            <Collapse in={openDetails[row.key] ?? (hasDetails(row) || detailError(index))}>
              <Stack gap="sm" mt="xs">
                <TextInput
                  size="md"
                  type="tel"
                  label="Phone guests can call"
                  placeholder="+1 403 555 0100"
                  value={row.phone}
                  onChange={(e) => {
                    const phone = e.currentTarget.value;
                    patch(index, { phone });
                  }}
                  error={errors[index]?.phone}
                  disabled={disabled}
                  maxLength={32}
                />
                <Textarea
                  size="md"
                  label="How to find it"
                  description="Shown to guests — directions, hours."
                  placeholder="e.g. Left of the main gate, by the flag. Open 10–6."
                  value={row.guestNote}
                  onChange={(e) => {
                    const guestNote = e.currentTarget.value;
                    patch(index, { guestNote });
                  }}
                  error={errors[index]?.guestNote}
                  disabled={disabled}
                  autosize
                  minRows={2}
                  maxRows={5}
                  maxLength={512}
                />
                {/* Where it is — an address, a pin on the map, or both (owner, 2026-10-23: "a lost & found in a park"). Set in
                    the map drawer: search, click, drag, or use this device's location when standing there. */}
                <Box>
                  <Text size="md" fw={500} mb={4}>
                    Where it is
                  </Text>
                  {row.latitude != null || row.address.trim() ? (
                    <Stack gap={2} mb="xs">
                      {row.address.trim() ? <Text size="md">{row.address}</Text> : null}
                      {row.latitude != null && row.longitude != null ? (
                        <Text size="md" c="dimmed" ff="monospace">
                          {row.latitude.toFixed(6)}, {row.longitude.toFixed(6)}
                        </Text>
                      ) : (
                        <Text size="md" c="dimmed">
                          No pin on the map
                        </Text>
                      )}
                    </Stack>
                  ) : (
                    <Text size="md" c="dimmed" mb="xs">
                      Guests get a map link to it.
                    </Text>
                  )}
                  <Group gap="xs">
                    <Button
                      variant="light"
                      size="md"
                      onClick={() => setPinRowKey(row.key)}
                      disabled={disabled}
                      leftSection={<AppIcon iconName="MapPin" size={16} />}
                    >
                      {row.latitude != null || row.address.trim() ? "Change on the map" : "Set on the map"}
                    </Button>
                    {row.latitude != null || row.address.trim() ? (
                      <Button variant="subtle" color="gray" size="md" onClick={() => patch(index, { latitude: null, longitude: null, address: "" })} disabled={disabled}>
                        Clear
                      </Button>
                    ) : null}
                  </Group>
                  {errors[index]?.latitude || errors[index]?.address ? (
                    <Text size="md" c="red" mt={4}>
                      {errors[index]?.latitude || errors[index]?.address}
                    </Text>
                  ) : null}
                </Box>
                <MediaImageField
                  intent="content-image"
                  allowUrl={false}
                  aspect={4 / 3}
                  label="Photo"
                  description="So guests recognize it."
                  addLabel="Add a photo"
                  value={row.photo}
                  onChange={(photo) => patch(index, { photo })}
                  error={errors[index]?.photoUploadedFileId}
                  disabled={disabled}
                />
              </Stack>
            </Collapse>
          </Box>
        </Paper>
      ))}

      <MapPinDrawer
        opened={!!pinRow}
        onClose={() => setPinRowKey(null)}
        title={pinRow?.name.trim() ? `Where is ${pinRow.name.trim()}?` : "Where is this point?"}
        value={{ latitude: pinRow?.latitude ?? null, longitude: pinRow?.longitude ?? null, address: pinRow?.address ?? "" }}
        center={mapCenter}
        onSave={(pin) => {
          if (pinIndex >= 0) {
            patch(pinIndex, { latitude: pin.latitude, longitude: pin.longitude, address: pin.address });
          }
        }}
      />

      <Box>
        <Button variant="light" size="md" onClick={add} disabled={disabled} leftSection={<AppIcon iconName="Plus" size={16} />}>
          Add point
        </Button>
      </Box>
    </Stack>
  );
};
